import { cache } from "react";
import type { ComponentRendering, Page } from "@sitecore-content-sdk/nextjs";
import type { SitecoreClient } from "@sitecore-content-sdk/nextjs/client";
import { fetchLayoutRouteData } from "./layout-route-service";

const HEADER_PLACEHOLDER = "headless-header";
const FOOTER_PLACEHOLDER = "headless-footer";

/**
 * Sitecore item shape we expect for the `LayoutRoute` droplink and the
 * `PartialDesigns` treelist on the referenced Page Design.
 */
type ItemRef = {
  id?: string | { value?: string };
  fields?: Record<string, unknown>;
};

const readItemId = (item: ItemRef | null | undefined): string | undefined => {
  if (!item) return undefined;
  if (typeof item.id === "string") return item.id;
  if (
    typeof item.id === "object" &&
    item.id !== null &&
    typeof item.id.value === "string"
  ) {
    return item.id.value;
  }
  return undefined;
};

const readPartialDesigns = (
  layoutRoute: ItemRef | null | undefined,
): ItemRef[] => {
  if (!layoutRoute?.fields) return [];
  const designs = layoutRoute.fields["PartialDesigns"];
  return Array.isArray(designs) ? (designs as ItemRef[]) : [];
};

/**
 * Cached layout fetch — identical (routeId, language) tuples within the same
 * server request resolve once. Across requests, Edge's own caching keeps the
 * response warm; rate-limited queries to Edge don't count cache hits.
 */
const fetchLayoutRouteCached = cache(
  (client: SitecoreClient, routeId: string, language: string) =>
    fetchLayoutRouteData(client, routeId, language),
);

/**
 * Apply the JSS-era "Layout Routing" pattern (Accelerate Cookbook recipe) to a
 * Content SDK 2 `Page`.
 *
 * Reads `page.layout.sitecore.route.fields.LayoutRoute` (a droplink to a Page
 * Design item) and walks its `PartialDesigns` treelist. For each partial
 * design, fetches its rendered layout and merges the partial's
 * `headless-header` / `headless-footer` placeholders into the main page's
 * placeholders.
 *
 * Skipped in editing/preview mode (the legacy plugin did the same): editors
 * still expect the directly-attached header/footer experience there.
 */
const PLACEHOLDERS = [HEADER_PLACEHOLDER, FOOTER_PLACEHOLDER] as const;

function mergePlaceholders(
  targetPlaceholders: Record<string, unknown>,
  sourcePlaceholders: Record<string, unknown>,
): void {
  PLACEHOLDERS.forEach((name) => {
    const partialContent = sourcePlaceholders[name] as
      | ComponentRendering[]
      | undefined;

    if (!partialContent?.length) {
      return;
    }

    const existing = (targetPlaceholders[name] ?? []) as ComponentRendering[];

    targetPlaceholders[name] = [...existing, ...partialContent];
  });
}

async function fetchPartialLayouts(
  partialDesigns: ItemRef[],
  client: SitecoreClient,
  language: string,
) {
  return Promise.all(
    partialDesigns.map((design) => {
      const id = readItemId(design);

      return id ? fetchLayoutRouteCached(client, id, language) : null;
    }),
  );
}

function mergePartialLayouts(
  targetPlaceholders: Record<string, unknown>,
  partialLayouts: Awaited<ReturnType<typeof fetchPartialLayouts>>,
): void {
  partialLayouts.forEach((layout) => {
    const placeholders = layout?.sitecore?.route?.placeholders;

    if (placeholders) {
      mergePlaceholders(targetPlaceholders, placeholders);
    }
  });
}

export const applyLayoutRoutes = async (
  page: Page,
  client: SitecoreClient,
  language: string,
): Promise<Page> => {
  const route = page.layout?.sitecore?.route;

  const canProcess =
    !page.mode.isEditing &&
    !page.mode.isPreview &&
    !!route?.fields &&
    !!route.placeholders;

  if (canProcess && route?.fields) {
    const layoutRoute = route.fields["LayoutRoute"] as
      | ItemRef
      | null
      | undefined;

    const partialDesigns = readPartialDesigns(layoutRoute);

    if (partialDesigns.length > 0) {
      const partialLayouts = await fetchPartialLayouts(
        partialDesigns,
        client,
        language,
      );

      mergePartialLayouts(route.placeholders, partialLayouts);
    }
  }

  return page;
};
